import {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {
  List,
  ListItem,
  Popover,
  Box,
  Typography,
  TextField,
  IconButton
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'

import './selectFilter.scss'

const SelectFilter = ({filter, onChange, options, column}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedValue, setSelectedValue] = useState(filter && filter.value ? filter.value : '')
  const [hoveredItem, setHoveredItem] = useState(null)

  useEffect(() => {
    if (filter && filter.value)
      setSelectedValue(filter.value)
    else
      setSelectedValue('')
  }, [filter])

  const handleClick = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleClear = event => {
    event.stopPropagation()
    setSelectedValue('')
    onChange('')
    setAnchorEl(null)
  }

  const handleChange = value => {
    if (value === '')
      onChange('')
    else
      onChange(value)

    setSelectedValue(value)
    setAnchorEl(null) // Close immediately after selection
  }

  const open = Boolean(anchorEl)
  const id = open ? 'select-filter-dropdown' : null

  // Format display text for the dropdown
  const getDisplayText = () => {
    if (selectedValue === '') {
      return 'Show All'
    } else {
      const selectedOption = options.find(opt => opt.id === selectedValue)
      return selectedOption ? selectedOption.name : selectedValue
    }
  }

  return (
    <div className='select-filter select-filter-dropdown'>
      <Box className='dropdown-trigger' onClick={handleClick}>
        <TextField
          variant='outlined'
          size='small'
          value={getDisplayText()}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <IconButton size='small' edge='end'>
                <KeyboardArrowDownIcon />
              </IconButton>
            )
          }}
        />
      </Box>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        slotProps={{
          paper: {
            className: 'filter-popover'
          }
        }}>
        <Box className='filter-header'>
          <Typography
            variant='body2'
            className='filter-title'
            sx={{
              fontWeight: 600,
              color: 'rgba(0, 0, 0, 0.87)',
              fontSize: '0.9rem',
              lineHeight: 1.5,
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              margin: '0 !important'
            }}>
            Select {column ? column.Header : 'Option'}
          </Typography>
          <IconButton
            size='small'
            onClick={handleClear}
            className='clear-button'
            aria-label='Clear selection'>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>

        <List className='filter-option-list'>
          <ListItem
            key='show-all'
            className={`filter-option ${selectedValue === '' ? 'selected' : ''} ${hoveredItem === '' ? 'hovered' : ''}`}
            onMouseEnter={() => setHoveredItem('')}
            onMouseLeave={() => setHoveredItem(null)}
            onClick={() => handleChange('')}
            disablePadding>
            <div className='option-content select-option'>
              <Typography variant='body2'>Show All</Typography>
              {selectedValue === '' && (
                <CheckIcon className='check-icon' fontSize='small' />
              )}
            </div>
          </ListItem>
          {options.map(option => {
            const value = option.id
            const label = option.name
            const isSelected = selectedValue === value
            const isHovered = hoveredItem === value

            return (
              <ListItem
                key={value}
                className={`filter-option ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''}`}
                onMouseEnter={() => setHoveredItem(value)}
                onMouseLeave={() => setHoveredItem(null)}
                onClick={() => handleChange(value)}
                disablePadding>
                <div className='option-content select-option'>
                  <Typography variant='body2'>{label}</Typography>
                  {isSelected && (
                    <CheckIcon className='check-icon' fontSize='small' />
                  )}
                </div>
              </ListItem>
            )
          })}
        </List>
      </Popover>
    </div>
  )
}

SelectFilter.propTypes = {
  filter: PropTypes.object,
  options: PropTypes.array,
  onChange: PropTypes.func,
  column: PropTypes.object
}

export default SelectFilter
